/**
 * TTS 适配器层类型契约（T10a）。
 *
 * 与 T03 的 Provider 抽象层保持同构：同一套错误码风格、同一套运行期依赖
 * （超时 / 重试 / 取消 / 错误规范化），只是把「对话流式分片」换成「整段音频」。
 *
 * 有意为之的差异：
 * - `TtsAdapter.speak()` 返回一次性结果 `{ audio, mime }`，而不是 AsyncIterable；
 * - 取消语义为「抛出 AbortError」，**绝不返回半截音频**（实现见 openai.ts）。
 */

import type { FetchLike } from '../providers/types';

/* ------------------------------------------------------------------ *
 * 请求 / 结果
 * ------------------------------------------------------------------ */

/** 支持的音频容器格式（OpenAI `response_format` 取值）。 */
export type TtsFormat = 'mp3' | 'opus' | 'aac' | 'flac' | 'wav' | 'pcm';

/** 合成一段语音的请求。 */
export interface TtsRequest {
  /** Local message association; never sent to upstream speech providers. */
  messageId?: string;
  /** 要朗读的文本。 */
  text: string;
  /** 音色标识；缺省时回退到 `ResolvedTtsConfig.defaultVoice`。 */
  voice?: string;
  /** 音频容器格式，映射到 OpenAI 的 `response_format`。默认 `mp3`。 */
  format?: TtsFormat;
  /** 语速倍率，映射到 OpenAI 的 `speed`。 */
  speed?: number;
  /**
   * 模型标识（如 `tts-1` / `gpt-4o-mini-tts`）。
   * 缺省时回退到 `ResolvedTtsConfig.defaultModel`；两者都没有则报 `config` 错误。
   */
  model?: string;
  /** 取消信号：中止底层请求并以 `AbortError` 结束。 */
  signal?: AbortSignal;
}

/** 合成结果。 */
export interface TtsResult {
  /** 音频二进制。 */
  audio: Blob;
  /** 音频 MIME 类型，如 `audio/mpeg`。 */
  mime: string;
}

/* ------------------------------------------------------------------ *
 * 配置
 * ------------------------------------------------------------------ */

export type TtsKind = 'openai-compatible' | 'custom';

export interface TtsConfig {
  id: string;
  kind: TtsKind;
  name: string;
  baseUrl: string;
  /** 密钥引用（如 `env:OPENAI_KEY`），解析由上层密钥存储负责。 */
  apiKeyRef: string;
  /** 默认音色；请求未指定 `voice` 时使用。 */
  defaultVoice?: string;
  /**
   * 默认模型（如 `tts-1`）。与 T03 `ProviderConfig.defaultModel` 对齐；
   * 请求未指定 `model` 时使用。
   */
  defaultModel?: string;
  enabled: boolean;
}

/** 运行期已解析出密钥的配置（`apiKeyRef` -> `apiKey` 由上层负责）。 */
export interface ResolvedTtsConfig extends TtsConfig {
  apiKey?: string;
  /** 追加到默认请求头上的自定义头。 */
  headers?: Record<string, string>;
  /** 覆盖适配器默认超时（毫秒）。 */
  timeoutMs?: number;
  /** 覆盖适配器默认重试次数（不含首次请求，默认 1）。 */
  retries?: number;
  /** 覆盖适配器默认重试间隔（毫秒）。 */
  retryDelayMs?: number;
}

/* ------------------------------------------------------------------ *
 * 适配器
 * ------------------------------------------------------------------ */

export interface TtsAdapter {
  readonly id: string;
  readonly kind: TtsKind;
  /** 列出该提供方可用音色。 */
  listVoices(): Promise<string[]>;
  /**
   * 合成整段语音。
   *
   * 约定：
   * - 成功：返回完整音频 `{ audio, mime }`；
   * - 取消（`signal` 触发）：以 `AbortError` 结束，**不返回任何音频**；
   * - HTTP / 超时 / 网络 / 解析错误：以 `TtsError` 抛出，携带规范化错误码。
   */
  speak(req: TtsRequest): Promise<TtsResult>;
}

/* ------------------------------------------------------------------ *
 * 错误
 * ------------------------------------------------------------------ */

/** TTS 错误码，与 T03 `ProviderErrorCode` 风格一致（同集合）。 */
export type TtsErrorCode =
  'http' | 'timeout' | 'network' | 'aborted' | 'parse' | 'config' | 'unsupported' | 'unknown';

/** 运行期可枚举的错误码清单（便于设置页展示与断言）。 */
export const TTS_ERROR_CODES: readonly TtsErrorCode[] = [
  'http',
  'timeout',
  'network',
  'aborted',
  'parse',
  'config',
  'unsupported',
  'unknown',
] as const;

/** TTS 层的规范化错误。取消不走这里，而是抛 `AbortError`。 */
export class TtsError extends Error {
  readonly code: TtsErrorCode;
  readonly status?: number;

  constructor(message: string, code: TtsErrorCode, status?: number) {
    super(message);
    this.name = 'TtsError';
    this.code = code;
    this.status = status;
  }
}

/* ------------------------------------------------------------------ *
 * 运行期依赖与工厂
 * ------------------------------------------------------------------ */

/** 可注入的 fetch 实现（默认取 globalThis.fetch）。 */
export type TtsFetchLike = FetchLike;

/** 适配器可复用的运行期依赖，与 T03 `ProviderDeps` 结构一致。 */
export interface TtsDeps {
  fetch?: TtsFetchLike;
  timeoutMs?: number;
  retries?: number;
  retryDelayMs?: number;
  extraHeaders?: Record<string, string>;
}

/** 适配器工厂：注册表按 kind 实例化适配器。 */
export type TtsAdapterFactory = (config: ResolvedTtsConfig, deps?: TtsDeps) => TtsAdapter;
